import { useLocation } from 'react-router-dom';
import { useAdminStatus } from '@/hooks/useAdminStatus';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import UsersManagement from '@/components/admin/UsersManagement';
import { LicensesManagement } from '@/components/admin/LicensesManagement';
import { TagMappingManagement } from '@/components/admin/TagMappingManagement';
import { BillingManagement } from '@/components/admin/BillingManagement';
import { ApiLogsManagement } from '@/components/admin/ApiLogsManagement';
import TrainingManagement from '@/components/admin/TrainingManagement';
import HelpManagement from '@/components/admin/HelpManagement';
import { SystemSettingsManagement } from '@/components/admin/SystemSettingsManagement';
import { ScriptTemplatesManagement } from '@/components/admin/ScriptTemplatesManagement';
import ScriptMergeRepair from '@/components/admin/ScriptMergeRepair';
import ActionTypesManagement from '@/components/admin/ActionTypesManagement';
import MessageCenterManagement from '@/components/admin/MessageCenterManagement';
import CampaignsManagement from '@/components/admin/CampaignsManagement';
import {
  Users,
  BarChart3,
  Settings,
  FileText,
  GraduationCap,
  HelpCircle,
  CreditCard,
  Tag,
  FileCode,
  Wrench,
  Zap,
  Mail,
  Phone,
} from 'lucide-react';

const AdminDashboard = () => {
  const location = useLocation();
  const { isAdmin, loading } = useAdminStatus();
  const currentPath = location.pathname;

  if (loading) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <div className='text-center'>
          <div className='animate-spin rounded-full h-32 w-32 border-b-2 border-primary'></div>
          <p className='mt-4 text-muted-foreground'>
            Loading admin dashboard...
          </p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <Card className='w-96'>
          <CardHeader>
            <CardTitle className='text-destructive'>Access Denied</CardTitle>
            <CardDescription>
              You don't have permission to access the admin dashboard.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const getBreadcrumbInfo = () => {
    const pathMap: Record<
      string,
      { title: string; icon: React.ReactNode; description: string }
    > = {
      '/admin/users': {
        title: 'Users',
        icon: <Users className='h-6 w-6' />,
        description: 'Manage user accounts, licenses, and permissions',
      },
      '/admin/licenses': {
        title: 'Licenses',
        icon: <BarChart3 className='h-6 w-6' />,
        description: 'Create and manage license levels and feature access',
      },
      '/admin/tag-mapping': {
        title: 'Tag Mapping',
        icon: <Tag className='h-6 w-6' />,
        description: 'Map Keap tags to license levels with priority rules',
      },
      '/admin/billing': {
        title: 'Billing',
        icon: <CreditCard className='h-6 w-6' />,
        description: 'View ThriveCart purchases and manage billing',
      },
      '/admin/api-logs': {
        title: 'API Usage Logs',
        icon: <FileText className='h-6 w-6' />,
        description: 'Monitor API usage and track failed calls',
      },
      '/admin/training-editor': {
        title: 'Training Editor',
        icon: <GraduationCap className='h-6 w-6' />,
        description: 'Create and manage training modules and content',
      },
      '/admin/help-center-editor': {
        title: 'Help Center Editor',
        icon: <HelpCircle className='h-6 w-6' />,
        description: 'Manage FAQ entries and help documentation',
      },
      '/admin/settings': {
        title: 'System Settings',
        icon: <Settings className='h-6 w-6' />,
        description: 'Configure global system settings and integrations',
      },
      '/admin/script-templates': {
        title: 'Script Templates',
        icon: <FileCode className='h-6 w-6' />,
        description: 'Manage script templates for inbound and outbound calls',
      },
      '/admin/script-repair': {
        title: 'Script Repair',
        icon: <Wrench className='h-6 w-6' />,
        description:
          'Fix scripts with unresolved merge tags using stored metadata',
      },
      '/admin/action-types': {
        title: 'Action Types',
        icon: <Zap className='h-6 w-6' />,
        description:
          'Configure display metadata for call action types from webhook tool calls',
      },
      '/admin/message-center': {
        title: 'Message Center',
        icon: <Mail className='h-6 w-6' />,
        description:
          'Configure email notifications, templates, and provider settings',
      },
      '/admin/campaigns': {
        title: 'Campaign Management',
        icon: <Phone className='h-6 w-6' />,
        description: 'Assign campaigns to clients for portal visibility',
      },
    };

    return pathMap[currentPath] || pathMap['/admin/users'];
  };

  const breadcrumbInfo = getBreadcrumbInfo();

  const renderContent = () => {
    switch (currentPath) {
      case '/admin/users':
        return <UsersManagement />;
      case '/admin/licenses':
        return <LicensesManagement />;
      case '/admin/tag-mapping':
        return <TagMappingManagement />;
      case '/admin/billing':
        return <BillingManagement />;
      case '/admin/api-logs':
        return <ApiLogsManagement />;
      case '/admin/training-editor':
        return <TrainingManagement />;
      case '/admin/help-center-editor':
        return <HelpManagement />;
      case '/admin/settings':
        return <SystemSettingsManagement />;
      case '/admin/script-templates':
        return <ScriptTemplatesManagement />;
      case '/admin/script-repair':
        return <ScriptMergeRepair />;
      case '/admin/action-types':
        return <ActionTypesManagement />;
      case '/admin/message-center':
        return <MessageCenterManagement />;
      case '/admin/campaigns':
        return <CampaignsManagement adminMode />;
      default:
        return <UsersManagement />;
    }
  };

  return (
    <div className='container mx-auto p-6 space-y-6'>
      {/* Breadcrumb Header */}
      <div className='flex items-center space-x-4 mb-8'>
        <div className='flex items-center space-x-2'>
          <Badge variant='secondary' className='text-sm'>
            Admin
          </Badge>
          <span className='text-muted-foreground'>/</span>
          <div className='flex items-center space-x-2'>
            {breadcrumbInfo.icon}
            <h1 className='text-3xl font-bold'>{breadcrumbInfo.title}</h1>
          </div>
        </div>
      </div>

      {/* Page Description */}
      <Card className='border-l-4 border-l-primary'>
        <CardContent className='pt-6'>
          <p className='text-muted-foreground'>{breadcrumbInfo.description}</p>
        </CardContent>
      </Card>

      {/* Main Content */}
      <div className='space-y-6'>{renderContent()}</div>
    </div>
  );
};

export default AdminDashboard;
